import { gql } from "@urql/core";
import { Client, CombinedError, cacheExchange, fetchExchange } from "urql";

import { GATEWAY_URL } from "./constants";
import { OnboardingContextFragment } from "./fragments/onboarding-context-fragment";
import { PaidChannelContextFragment } from "./fragments/paid-channel-context-fragment";
import { SitesFragment } from "./fragments/sites-fragment";
import { UserFootprintsFragment } from "./fragments/user-footprints-fragment";
import {
    GetGrowthUnifiedProfileArgs,
    GetGrowthUnifiedProfileResponse,
    GrowthUnifiedProfileClientOptions,
    OptionalFields,
    UpdateGrowthUnifiedProfileResponse
} from "./types";
import { CreateUnifiedProfileInput } from "./unified-profile.schema";

export class GrowthUnifiedProfileClient {
    private readonly client: Client;
    private readonly fetchOptions;

    public constructor(options?: GrowthUnifiedProfileClientOptions) {
        this.fetchOptions = options?.slauthToken
            ? {
                  headers: {
                      Authorization: `SLAUTH ${options.slauthToken || ""}`
                  }
              }
            : {
                  credentials: "include" as RequestCredentials
              };
        this.client = new Client({
            url: GATEWAY_URL,
            exchanges: [cacheExchange, fetchExchange],
            fetchOptions: this.fetchOptions
        });
    }
    private constructProfileQuery(optionalFields?: OptionalFields[]) {
        const fragments = [
            optionalFields?.includes(OptionalFields.ONBOARDING_CONTEXT)
                ? "...OnboardingContextFragment"
                : "",
            optionalFields?.includes(OptionalFields.SITES)
                ? "...SitesFragment"
                : "",
            optionalFields?.includes(OptionalFields.PAID_CHANNEL_CONTEXT)
                ? "...PaidChannelContextFragment"
                : "",
            optionalFields?.includes(OptionalFields.USER_FOOTPRINTS)
                ? "...UserFootprintsFragment"
                : ""
        ]
            .filter(Boolean)
            .join("\n");

        return fragments;
    }
    /**
     * Get a unified profile
     * @param accountId
     * @returns { data, error }
     */
    public getProfile = async (
        args: GetGrowthUnifiedProfileArgs,
        optionalFields?: OptionalFields[],
        optionalHeaderUseCase?: string
    ) => {
        const { accountId, anonymousId, tenantId } = args;
        const fragments = this.constructProfileQuery(optionalFields);

        const GET_PROFILE_QUERY = gql`
            query getUnifiedProfile($accountId: ID, $tenantId: ID, $anonymousId: ID) {
                growthUnifiedProfile_getUnifiedProfile(
                    accountId: $accountId
                    tenantId: $tenantId
                    anonymousId: $anonymousId
                ) {
                    ${fragments}
                }
            }
            ${
                optionalFields?.includes(OptionalFields.ONBOARDING_CONTEXT)
                    ? OnboardingContextFragment
                    : ""
            }
            ${
                optionalFields?.includes(OptionalFields.SITES)
                    ? SitesFragment
                    : ""
            }
            ${
                optionalFields?.includes(OptionalFields.PAID_CHANNEL_CONTEXT)
                    ? PaidChannelContextFragment
                    : ""
            }
            ${
                optionalFields?.includes(OptionalFields.USER_FOOTPRINTS)
                    ? UserFootprintsFragment
                    : ""
            }
        `;
        const fetchContext = {
            ...this.fetchOptions, // Include fetchOptions from the constructor
            headers: {
                ...(this.fetchOptions?.headers || {}), // Include existing headers
                ...(optionalHeaderUseCase
                    ? { "x-use-case": optionalHeaderUseCase }
                    : {}) // Add optional header
            }
        };

        const {
            data,
            error
        }: {
            data?: GetGrowthUnifiedProfileResponse;
            error?: CombinedError | undefined;
        } = await this.client.query(
            GET_PROFILE_QUERY,
            {
                accountId,
                tenantId,
                anonymousId
            },
            {
                fetchOptions: fetchContext
            }
        );

        return { data: data?.growthUnifiedProfile_getUnifiedProfile, error };
    };

    /**
     * Create or update a unified profile
     * @param profile
     * @returns { data, error }
     */
    public updateProfile = async ({
        profile
    }: {
        profile: Partial<CreateUnifiedProfileInput>;
    }) => {
        // Currently accountId is required, once anonymousId support
        // is added to the upstream service, this check can be removed
        if (!profile.accountId) {
            throw new Error("accountId is required");
        }

        const UPDATE_PROFILE_MUTATION = gql`
            mutation createUnifiedProfile(
                $profile: GrowthUnifiedProfileCreateProfileInput!
            ) {
                growthUnifiedProfile_createUnifiedProfile(profile: $profile) {
                    ...OnboardingContextFragment
                }
            }
            ${OnboardingContextFragment}
        `;

        const {
            data,
            error
        }: {
            data?: UpdateGrowthUnifiedProfileResponse;
            error?: CombinedError | undefined;
        } = await this.client.mutation(UPDATE_PROFILE_MUTATION, {
            profile
        });

        return { data: data?.growthUnifiedProfile_createUnifiedProfile, error };
    };
}
